import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Boxes, TrendingUp, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoUrl from "/milkymoo.webp?url";
import { useMarca } from "@/hooks/useMarca";
import devConfig from "@/../config/dev.config.json";
import { obterClienteDados } from "@/lib/obter-dados";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Milky Moo Palmares — Controle de estoque para sorveteria" },
      {
        name: "description",
        content:
          "Controle de insumos, receitas de milk shakes, vendas, alertas de estoque baixo e custos da sua sorveteria em um só lugar.",
      },
      { property: "og:title", content: "Milky Moo Palmares — Controle de estoque para sorveteria" },
      {
        property: "og:description",
        content:
          "Insumos, receitas por produto, baixa automática por venda e avisos de estoque baixo.",
      },
    ],
  }),
  component: RootEntry,
});

function RootEntry() {
  const navigate = useNavigate();
  const [checando, setChecando] = useState(true);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const banco = await obterClienteDados();
        const sess = await (banco as any).auth.getSession();
        if (!ativo) return;
        if (sess?.data?.session) navigate({ to: "/inicio" });
        else navigate({ to: "/auth" });
      } catch (e) {
        // Em erro, leva ao login por segurança.
        try {
          navigate({ to: "/auth" });
        } catch {}
      } finally {
        if (ativo) setChecando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [navigate]);

  if (checando)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-[10px] text-center">
        <img src={logoUrl} alt="" className="w-64 max-w-[80vw] object-contain" />
        <p className="text-sm text-muted-foreground">Verificando seu acesso…</p>
      </main>
    );

  return null;
}

const features = [
  {
    icon: Boxes,
    title: "Insumos e receitas",
    text: "Cada produto com seus insumos e quantidades.",
  },
  {
    icon: TrendingUp,
    title: "Baixa por venda",
    text: "Informe os produtos vendidos e o sistema totaliza o consumo.",
  },
  {
    icon: Bell,
    title: "Avisos de estoque",
    text: "Alertas de estoque baixo no depósito e na loja.",
  },
];

function Landing() {
  const navigate = useNavigate();
  const [verificando, setVerificando] = useState(true);
  const { nomeApp } = useMarca();
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [showDebug, setShowDebug] = useState(false);

  // Depois do login com Google o navegador volta para esta página.
  // Se já existe uma sessão, seguimos direto para dentro do sistema.
  useEffect(() => {
    let ativo = true;
    let sub: any = undefined;
    (async () => {
      try {
        if ((devConfig as any)?.mostrarLogConsole !== false)
          console.log("[Landing] iniciar verificacao de sessao");
      } catch {}
      const banco = await obterClienteDados();
      try {
        if ((devConfig as any)?.mostrarLogConsole !== false)
          console.log("[Landing] obterClienteDados resolved", !!banco);
      } catch {}
      sub = (banco as any).auth.onAuthStateChange((_e: any, s: any) => {
        try {
          if ((devConfig as any)?.mostrarLogConsole !== false)
            console.log("[Landing] onAuthStateChange event", !!s);
        } catch {}
        if (s) navigate({ to: "/inicio" });
      });
      (banco as any).auth.getSession().then(({ data }: any) => {
        try {
          if ((devConfig as any)?.mostrarLogConsole !== false)
            console.log("[Landing] getSession resolved", !!data?.session);
        } catch {}
        if (!ativo) return;
        if (data.session) navigate({ to: "/inicio" });
        else setVerificando(false);
      });
    })();
    return () => {
      ativo = false;
      // subscription may be unset if we didn't import the client yet
      try {
        if (sub?.subscription) sub.subscription.unsubscribe();
      } catch (e) {
        /* noop */
      }
    };
  }, [navigate]);

  // If we've finished verification and no session was found, redirect to
  // the auth screen so the login is the initial view.
  useEffect(() => {
    if (!verificando) {
      navigate({ to: "/auth" });
    }
  }, [verificando, navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("_dbg")) return;
    setShowDebug(true);
    const read = async () => {
      try {
        const dbg = (window as any).__OBTER_DADOS_DEBUG ?? null;
        const authExists = !!(window as any).__OBTER_DADOS_AUTH;
        const { getLocalItem, getSessionItem } = await import("@/lib/storage-safe");
        const local = getLocalItem("auth-externo-sessao");
        const session = getSessionItem("auth-externo-sessao");
        setDebugInfo({ dbg, authExists, local: !!local, session: !!session });
      } catch (e) {
        setDebugInfo({ error: String(e) });
      }
    };
    void read();
    const id = setInterval(() => void read(), 1500);
    return () => clearInterval(id);
  }, []);

  if (verificando)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-[10px] text-center">
        <img src={logoUrl} alt={nomeApp} className="w-64 max-w-[80vw] object-contain" />
        <p className="text-sm text-muted-foreground">Verificando seu acesso…</p>
      </main>
    );

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-[10px] py-20">
        <div className="flex items-center gap-3 text-primary">
          <img src={logoUrl} alt={nomeApp} className="h-10 w-10 object-contain" />
          <span className="font-display text-lg font-semibold">{nomeApp}</span>
        </div>

        <h1 className="mt-12 max-w-3xl text-5xl leading-tight font-semibold text-foreground sm:text-6xl">
          O estoque da sua sorveteria sob controle.
        </h1>
        <p className="mt-6 max-w-xl text-lg text-muted-foreground">
          Cadastre insumos, monte a receita de cada milk shake, registre as vendas e receba avisos
          quando algo estiver acabando.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <Link to="/auth">Entrar no sistema</Link>
          </Button>
        </div>

        <div className="mt-20 grid gap-5 sm:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="card-soft p-6">
              <f.icon className="h-6 w-6 text-primary" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">{f.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
      {showDebug && (
        <div style={{ position: "fixed", right: 12, bottom: 12, zIndex: 9999 }}>
          <div
            style={{
              background: "rgba(0,0,0,0.8)",
              color: "#fff",
              padding: 10,
              borderRadius: 6,
              fontSize: 12,
              maxWidth: 480,
              maxHeight: 320,
              overflow: "auto",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <strong>Debug</strong>
              <button
                onClick={() => setShowDebug(false)}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.1)",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                Close
              </button>
            </div>
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </main>
  );
}
